/**
 * A screenshot on the public site, framed as a browser window.
 *
 * Shots are `src/assets/marketing/<name>.webp`, collected at build time. A name
 * with no file renders nothing, so a page never shows a broken image while a
 * shot is still to be taken.
 */

import { cn } from '@/utils/cn';
import { SHOTS } from '@/utils/marketingShots';

interface MarketingShotProps {
  name: string;
  /** Alt text; also the caption when `caption` is set. */
  alt: string;
  caption?: boolean;
  className?: string;
  /** The hero image loads straight away; everything else waits until scrolled to. */
  eager?: boolean;
}

export default function MarketingShot({ name, alt, caption, className, eager }: MarketingShotProps) {
  const url = SHOTS[name];
  if (!url) return null;
  return (
    <figure className={cn('min-w-0', className)}>
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-large">
        <div className="flex items-center gap-1.5 border-b border-neutral-200 bg-neutral-100 px-3 py-2" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </div>
        <img
          src={url}
          alt={alt}
          width={1440}
          height={900}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="block h-auto w-full"
        />
      </div>
      {caption && <figcaption className="mt-3 text-sm text-neutral-600">{alt}</figcaption>}
    </figure>
  );
}
